import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  collection, 
  getDocs, 
  doc, 
  getDoc,
  updateDoc,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../../firebase';

const SupportCaseManagement = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [supportCases, setSupportCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [users, setUsers] = useState({});
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    priority: '',
    category: ''
  });
  const [editingCase, setEditingCase] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const statusOptions = ['open', 'in-progress', 'waiting-for-customer', 'resolved', 'closed'];
  const priorityOptions = ['Low', 'Medium', 'High', 'Critical'];
  const categoryOptions = [
    'Account Issues',
    'Payment & Billing',
    'Technical Support',
    'Session Problems',
    'Profile Management',
    'Feature Request',
    'Bug Report',
    'Other'
  ];

  useEffect(() => {
    if (currentUser) {
      checkAdminStatus();
    } else {
      navigate('/login');
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    applyFilters();
  }, [supportCases, filters]);

  const checkAdminStatus = async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists() && userDoc.data().isAdmin) {
        setIsAdmin(true);
        fetchData();
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      navigate('/');
    }
  };

  const fetchData = async () => {
    try {
      // Fetch support cases and users in parallel
      const [casesSnapshot, usersSnapshot] = await Promise.all([
        getDocs(collection(db, 'supportCases')),
        getDocs(collection(db, 'users'))
      ]);

      // Build users lookup
      const usersData = {};
      usersSnapshot.forEach((doc) => {
        usersData[doc.id] = { id: doc.id, ...doc.data() };
      });

      // Build support cases data
      const casesData = [];
      casesSnapshot.forEach((doc) => {
        const caseData = { id: doc.id, ...doc.data() };
        
        // Add user information
        const user = usersData[caseData.userId];
        caseData.userName = user ? `${user.firstName} ${user.lastName}` : 'Unknown User';
        caseData.userEmail = user?.email || 'Unknown';

        casesData.push(caseData);
      });

      // Sort by creation date (newest first)
      casesData.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
        const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
        return bTime - aTime;
      });

      setUsers(usersData);
      setSupportCases(casesData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...supportCases];

    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(supportCase => 
        supportCase.subject?.toLowerCase().includes(searchTerm) ||
        supportCase.description?.toLowerCase().includes(searchTerm) ||
        supportCase.userName?.toLowerCase().includes(searchTerm) ||
        supportCase.userEmail?.toLowerCase().includes(searchTerm) ||
        supportCase.caseNumber?.toLowerCase().includes(searchTerm)
      );
    }

    // Status filter
    if (filters.status) {
      filtered = filtered.filter(supportCase => supportCase.status === filters.status);
    }

    // Priority filter
    if (filters.priority) {
      filtered = filtered.filter(supportCase => supportCase.priority === filters.priority);
    }

    // Category filter
    if (filters.category) {
      filtered = filtered.filter(supportCase => supportCase.category === filters.category);
    }

    setFilteredCases(filtered);
  };

  const handleEditCase = (supportCase) => {
    setEditingCase({ ...supportCase });
    setShowEditModal(true);
  };

  const handleSaveCase = async () => {
    try {
      const caseRef = doc(db, 'supportCases', editingCase.id);
      const updateData = {
        subject: editingCase.subject,
        description: editingCase.description,
        status: editingCase.status,
        priority: editingCase.priority,
        category: editingCase.category,
        resolution: editingCase.resolution || '',
        updatedAt: new Date()
      };

      await updateDoc(caseRef, updateData);
      
      // Update local state
      setSupportCases(supportCases.map(supportCase => 
        supportCase.id === editingCase.id ? { ...supportCase, ...updateData } : supportCase
      ));
      
      setShowEditModal(false);
      setEditingCase(null);
    } catch (error) {
      console.error('Error updating support case:', error);
      alert('Error updating support case: ' + error.message);
    }
  };

  const handleDeleteCase = async (caseId) => {
    if (!window.confirm('Are you sure you want to delete this support case? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'supportCases', caseId));
      setSupportCases(supportCases.filter(supportCase => supportCase.id !== caseId));
    } catch (error) {
      console.error('Error deleting support case:', error);
      alert('Error deleting support case: ' + error.message);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical': return '#dc3545';
      case 'high': return '#fd7e14';
      case 'medium': return '#ffc107';
      case 'low': return '#28a745';
      default: return '#6c757d';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return '#007bff';
      case 'in-progress': return '#ffc107';
      case 'waiting-for-customer': return '#17a2b8';
      case 'resolved': return '#28a745';
      case 'closed': return '#6c757d';
      default: return '#6c757d';
    }
  };

  if (loading) {
    return <div className="loading">Loading support cases...</div>;
  }

  if (!isAdmin) {
    return <div className="error">Access denied. Admin privileges required.</div>;
  }

  return (
    <div className="admin-support-management">
      <div className="admin-header">
        <div className="admin-breadcrumb">
          <button onClick={() => navigate('/admin/dashboard')} className="breadcrumb-link">
            ← Back to Admin Dashboard
          </button>
        </div>
        <h2>Support Case Management</h2>
        <p>Manage user support requests and cases</p>
      </div>

      <div className="support-filters">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Search cases..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="support-search-input"
          />
        </div>

        <div className="filter-group">
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="support-filter-select"
          >
            <option value="">All Statuses</option>
            {statusOptions.map(status => (
              <option key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <select
            value={filters.priority}
            onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
            className="support-filter-select"
          >
            <option value="">All Priorities</option>
            {priorityOptions.map(priority => (
              <option key={priority} value={priority}>
                {priority.charAt(0).toUpperCase() + priority.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <select
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            className="support-filter-select"
          >
            <option value="">All Categories</option>
            {categoryOptions.map(category => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="admin-content">
        <div className="support-cases-stats">
          <div className="support-stat-item">
            <span className="support-stat-label">Total Cases:</span>
            <span className="support-stat-value">{supportCases.length}</span>
          </div>
          <div className="support-stat-item">
            <span className="support-stat-label">Filtered:</span>
            <span className="support-stat-value">{filteredCases.length}</span>
          </div>
        </div>

        <div className="support-cases-grid">
          {filteredCases.map(supportCase => (
            <div key={supportCase.id} className="support-case-card">
              <div className="case-card-header">
                <div className="case-number-badge">
                  {supportCase.caseNumber}
                </div>
                <div className="case-badges">
                  <span 
                    className="support-priority-badge"
                    style={{ 
                      backgroundColor: getPriorityColor(supportCase.priority),
                      color: supportCase.priority?.toLowerCase() === 'medium' ? '#000' : '#fff'
                    }}
                  >
                    {supportCase.priority}
                  </span>
                  <span 
                    className="support-status-badge"
                    style={{ backgroundColor: getStatusColor(supportCase.status) }}
                  >
                    {supportCase.status?.replace('-', ' ')}
                  </span>
                </div>
              </div>
              
              <div className="case-card-content">
                <div className="case-subject-title">{supportCase.subject}</div>
                <div className="case-description-preview">
                  {supportCase.description?.substring(0, 150)}
                  {supportCase.description?.length > 150 && '...'}
                </div>
              </div>
              
              <div className="case-card-meta">
                <div className="case-user-info">
                  <div className="user-name">{supportCase.userName}</div>
                  <div className="user-email">{supportCase.userEmail}</div>
                </div>
                <div className="case-category">
                  <span className="category-tag">
                    {supportCase.category}
                  </span>
                </div>
              </div>
              
              <div className="case-card-footer">
                <div className="case-created-date">
                  {formatDate(supportCase.createdAt)}
                </div>
                <div className="case-actions">
                  <button 
                    className="support-edit-btn"
                    onClick={() => handleEditCase(supportCase)}
                  >
                    Edit
                  </button>
                  <button 
                    className="support-delete-btn"
                    onClick={() => handleDeleteCase(supportCase.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit Case Modal */}
      {showEditModal && editingCase && (
        <div className="modal-overlay">
          <div className="modal-content edit-case-modal">
            <div className="modal-header">
              <h3>Edit Support Case</h3>
              <button 
                className="modal-close-btn"
                onClick={() => setShowEditModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="case-info">
                <div className="info-item">
                  <strong>Case Number:</strong> {editingCase.caseNumber}
                </div>
                <div className="info-item">
                  <strong>User:</strong> {editingCase.userName} ({editingCase.userEmail})
                </div>
                <div className="info-item">
                  <strong>Created:</strong> {formatDate(editingCase.createdAt)}
                </div>
              </div>

              <div className="form-group">
                <label>Subject</label>
                <input
                  type="text"
                  value={editingCase.subject || ''}
                  onChange={(e) => setEditingCase({
                    ...editingCase,
                    subject: e.target.value
                  })}
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={editingCase.description || ''}
                  onChange={(e) => setEditingCase({
                    ...editingCase,
                    description: e.target.value
                  })}
                  rows="4"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={editingCase.category || ''}
                    onChange={(e) => setEditingCase({
                      ...editingCase,
                      category: e.target.value
                    })}
                  >
                    {categoryOptions.map(category => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Priority</label>
                  <select
                    value={editingCase.priority || 'Medium'}
                    onChange={(e) => setEditingCase({
                      ...editingCase,
                      priority: e.target.value
                    })}
                  >
                    {priorityOptions.map(priority => (
                      <option key={priority} value={priority}>
                        {priority}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  value={editingCase.status || 'open'}
                  onChange={(e) => setEditingCase({
                    ...editingCase,
                    status: e.target.value
                  })}
                >
                  {statusOptions.map(status => (
                    <option key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Resolution (Admin Only)</label>
                <textarea
                  value={editingCase.resolution || ''}
                  onChange={(e) => setEditingCase({
                    ...editingCase,
                    resolution: e.target.value
                  })}
                  rows="4"
                  placeholder="Enter resolution details or leave blank if not resolved..."
                />
                <div className="field-note">
                  This field is only visible and editable by admins. Users can see this when filled out.
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button 
                className="cancel-btn"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
              <button 
                className="save-btn"
                onClick={handleSaveCase}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportCaseManagement;